import UserClientData from "../../../core/client-data/user/user"
import ProjectLocalSettings from "../project/project-local-settings"
import ViewTemplate from "./view-template"
import ProjectCollectionLocalSettings from "../project-collection/project-collection-local-settings"
import {ViewType} from "../../gd/const/views/view-type"
import {getViewSystemType} from "../../gd/const/views/get-view-system-type"
import {DEFAULT_TEMPLATE_ID} from "../app-settings/app-settings"
import ViewTemplates_createDefaultTemplates from "./functions/create-default-template"
import {areJSONObjectPropsEqual} from "../../gd/utils/compare"

// const debug = "ViewTemplates"
const debug = false

/*
this.viewTemplates = [
{
        id: template_id1,
        name: template_name1,
        viewSystemType: view_system_type_1,
        access: 'private',
        userId: some_user_id,
        appSettings: {...}
        isDefault: true
},
{
        id: template_id2,
        name: template_name2,
        viewSystemType: view_system_type_2,
        access: 'public',
        userId: some_user_id,
        appSettings: {...}
    }, ...]
*/

/*
RIGHT NOW WE SUPPORT VIEW TEMPLATES FOR:
- task list
- task table
- board
- calendar
- gantt
- workload

- projects table
... enything else ?
 */

export default class ViewTemplates {

    public viewTemplates        : ViewTemplate[] = []
    private defaultTemplates    : Partial<Record<ViewType, ViewTemplate>> = ViewTemplates_createDefaultTemplates()

    public viewTypes = [
        ViewType.PROJECT_LIST,
        ViewType.PROJECT_TABLE,
        ViewType.PROJECT_BOARD,
        ViewType.PROJECT_CALENDAR,
        ViewType.PROJECT_GANTT,
        ViewType.PROJECT_WORKLOAD,
        ViewType.PROJECT_DOCS,
        ViewType.PROJECT_PAGE,
        ViewType.PROJECT_SUMMARY,
        ViewType.PROJECT_TABLE_MAP,

        ViewType.CUSTOM_VIEW_TASKS_TABLE,
        ViewType.CUSTOM_VIEW_PROJECTS_TABLE,

        ViewType.PORTFOLIO_SUMMARY,
    ]

    constructor() {
        // retrieve company view templates (private)
        const { clientData } = gd.session.me;
        const userTemplates = clientData.viewTemplates ? clientData.viewTemplates : [];

        const allTemplatesConfig = _.union(
            gd.company.settings.viewTemplates,
            userTemplates
        )

        this.viewTemplates = []
        _.forEach(allTemplatesConfig, (viewTemplateConfig: any)=>{
            const viewTemplate = new ViewTemplate(viewTemplateConfig)
            this.viewTemplates.push(viewTemplate)
        })

        this._normalizeDefaultTemplates()
    }

    _normalizeDefaultTemplates() {
        // this creates default templates if they were not created before (!)

        _.map(this.viewTypes, (viewTemplateSystemType: ViewType) => {
            let defaultViewTemplate = this.getDefault(viewTemplateSystemType)
            if (!defaultViewTemplate) {
                defaultViewTemplate = this.defaultTemplates[viewTemplateSystemType]
                if (defaultViewTemplate) {
                    this.viewTemplates.push(defaultViewTemplate)
                }
            }
        })
    }

    getDefault(systemType:ViewType) {
        // return degfault template for systemType
        return _.find(this.viewTemplates, template=>{
            return template.viewSystemType === systemType && template.isDefault
        })
    }

    getTemplate(systemType:ViewType, templateId:ID) {

        debug && console.log(debug, "getTemplate", systemType, templateId, this.viewTemplates)

        /*let searchSystemType : any
        switch (systemType) {
            case ViewType.CUSTOM_VIEW_TASKS_TABLE:
                searchSystemType = ViewType.PROJECT_TABLE;
                // CUSTOM_VIEW_TASKS_TABLE has only custom default template, but shares all TASK_TABLE templates
                break
            case ViewType.PROJECTS_PROJECTS_LIST:
            case ViewType.CUSTOM_VIEW_PROJECTS_TABLE:
                searchSystemType = ViewType.CUSTOM_VIEW_PROJECTS_TABLE  // Oni oba ispolzuet preseti is CUSTOM_VIEW_PROJECTS_TABLE
                break
            default:
                searchSystemType = systemType
        }*/
        // use getViewSystemType for type mapping (does all the above and more)
        const searchSystemType: ViewType = getViewSystemType(systemType)

        let template = _.find(this.viewTemplates, template=>{
            return template.viewSystemType === searchSystemType && template.id === templateId
        })

        if (!template) template = this.getDefault(systemType)

        return template
    }

    getViewTemplates(viewSystemType:ViewType) {
        return _.chain(this.viewTemplates)
            .filter(template=>
                template.viewSystemType === viewSystemType &&
                (template.access === 'public' || template.userId === gd.session.me.id))
            .orderBy([template=>template.isDefault ? 0 : 1, template=>template.name], ['asc', 'asc'])
            .value();
    }

    addTemplate(name:string, access:string, systemType: ViewType, appSettings:any) {
        const newTemplate = new ViewTemplate({
            id              : this._generateViewId(),
            name            : name,
            viewSystemType  : systemType,
            access          : access,
            userId          : gd.session.me.id,
            appSettings     : appSettings
        })

        this.viewTemplates.push(newTemplate);

        this._save();

        return newTemplate;
    }

    addTemplateFromProjectView(name:string, access:string, view:any, systemType: ViewType) {
        debug && console.log(debug, "addTemplateFromProjectView()")

        let projectLocalSettings, appSettings;

        // if we save current view as template the we need to check if appSettings
        // in project local settings were overridden
        const isCurrentView = gd.projectViews.currentView.id === view.id;
        if (isCurrentView) {
            // check base view local settings nad retrieve appSettings
            projectLocalSettings = new ProjectLocalSettings(view.projectViews.project.id);
            appSettings = projectLocalSettings.views[view.id] ? projectLocalSettings.views[view.id].appSettings : null;
        }

        // if no appSettings were set (from local settings) then set baseView appSettings
        if (!appSettings) appSettings = view.appSettings
        appSettings = _.omit(appSettings, 'templateId');

        return this.addTemplate(name, access, systemType, appSettings);
    }

    addTemplateFromProjectCollectionView(name:string, access:string, view:any) {
        debug && console.log(debug, "addTemplateFromProjectCollectionView")

        let projectCollectionLocalSettings, appSettings;

        // if we save current view as template then we need to check if appSettings
        // in project local settings were overridden
        const isCurrentView = gd.projectCollectionViews.currentView?.id === view.id;
        if (isCurrentView) {
            // check base view local settings nad retrieve appSettings
            projectCollectionLocalSettings = new ProjectCollectionLocalSettings(view.projectCollectionId);
            appSettings = projectCollectionLocalSettings.views[view.id] ? projectCollectionLocalSettings.views[view.id].appSettings : null;
        }

        // if no appSettings were set (from local settings) then set baseView appSettings
        if (!appSettings) appSettings = view.appSettings
        appSettings = _.omit(appSettings, 'templateId');

        return this.addTemplate(name, access, getViewSystemType(view.systemType), appSettings);
    }

    editTemplate(templateId:ID, name:string|null, access:string|null, viewSystemType: ViewType, appSettings:any) {
        const template : any = _.remove(this.viewTemplates, template => template.viewSystemType === viewSystemType && template.id === templateId)[0]

        // Create a new ViewTemplate instance to prevent mutating the original template by reference
        // (e.g., from this.defaultTemplates when editing a default template)
        const newTemplate = new ViewTemplate(template.toJSON())
        if (name)           newTemplate.name = name
        if (access)         newTemplate.access = access
        if (appSettings)    newTemplate.appSettings = appSettings

        this.viewTemplates.push(newTemplate)

        this._save()

        return newTemplate
    }

    editTemplateFromProjectView(templateId:ID, name:string|null, access:string|null, view:any, systemType: ViewType) {
        debug && console.log(debug, "editTemplateFromProjectView()")

        let projectLocalSettings, appSettings;

        // if we save current view as template the we need to check if appSettings
        // in project local settings were overridden
        const isCurrentView = gd.projectViews.currentView.id === view.id;
        if (isCurrentView) {
            // check base view local settings nad retrieve appSettings
            projectLocalSettings = new ProjectLocalSettings(view.projectViews.project.id);
            appSettings = projectLocalSettings.views[view.id] ? projectLocalSettings.views[view.id].appSettings : null;
        }

        // if no appSettings were set (from local settings) then set baseView appSettings
        if (!appSettings) appSettings = view.appSettings
        appSettings = _.omit(appSettings, 'templateId');

        return this.editTemplate(templateId, name, access, systemType, appSettings);
    }

    editTemplateFromProjectCollectionView(templateId:ID, name:string|null, access:string|null, view:any) {
        debug && console.log(debug, "editTemplateFromProjectCollectionView", view)

        let projectCollectionLocalSettings, appSettings

        // if we save current view as template then we need to check if appSettings
        // in project collection local settings were overridden
        const isCurrentView = gd.projectCollectionViews.currentView?.id === view.id
        if (isCurrentView) {
            // check base view local settings nad retrieve appSettings
            projectCollectionLocalSettings = new ProjectCollectionLocalSettings(view.projectCollectionId)
            appSettings = projectCollectionLocalSettings.views[view.id] ? projectCollectionLocalSettings.views[view.id].appSettings : null

            debug && console.log(debug, "editTemplateFromProjectCollectionView [B]", appSettings, projectCollectionLocalSettings.views[view.id])
        }

        // if no appSettings were set (from local settings) then set baseView appSettings
        if (!appSettings) appSettings = view.appSettings
        appSettings = _.omit(appSettings, 'templateId')

        return this.editTemplate(templateId, name, access, getViewSystemType(view.systemType), appSettings)
    }

    removeTemplate(templateId:ID) {
        _.remove(this.viewTemplates, template => template.id === templateId);

        this._save();
    }

    _save() {
        debug && console.log(debug, "_save()")

        // save company view templates
        if (gd.ac.check.company.isManager(gd.companyId)) {
            const companyTemplatesConfig : any[] = []

            const companyTemplates = _.filter(this.viewTemplates, (template: ViewTemplate) => {
                // skip if not a public view template
                if (template.access !== 'public') {
                    return false
                }

                // save if it's not a default template
                if (template.id !== DEFAULT_TEMPLATE_ID) {
                    return true
                }

                /** --- for default templates, save only if altered (not equal to original) --- **/
                const companyTemplateJSON   = template.toJSON()
                delete companyTemplateJSON.appSettings.templateId
                const defaultTemplateJSON   = (this.defaultTemplates[template.viewSystemType as keyof {}] as ViewTemplate)?.toJSON()
                return !areJSONObjectPropsEqual(companyTemplateJSON, defaultTemplateJSON)
            })

            _.map(companyTemplates, (companyViewTemplate: ViewTemplate) => {
                companyTemplatesConfig.push(companyViewTemplate.toJSON())
            })

            gd.company.settings.viewTemplates = companyTemplatesConfig
            gd.company.settings.save()
        }

        //save user view templates (private)
        const { clientData } = gd.session.me;
        clientData.viewTemplates = _.filter(this.viewTemplates, template=>template.access === 'private');
        gd.session.me.clientData = UserClientData.normalize(clientData);

        gd.api.MyAccount.updateClientData({clientData: gd.session.me.clientData});
    }

    _generateViewId() {
        // get existing ids
        const arrayIds = _.map(this.viewTemplates, (view=>view.id));
        // generate
        return gd.utils.generateUIDWithCollisionChecking(arrayIds);
    }

    /*static getTemplateSystemType = (viewType:ViewType):ViewType => {

        switch (viewType) {
            case ViewType.PORTFOLIO_LIST:
            case ViewType.PROJECT_LIST:
                return ViewType.PROJECT_LIST

            case ViewType.PORTFOLIO_TABLE:
            case ViewType.PROJECT_TABLE:
                return ViewType.PROJECT_TABLE

            case ViewType.PORTFOLIO_BOARD:
            case ViewType.PROJECT_BOARD:
                return ViewType.PROJECT_BOARD

            case ViewType.PORTFOLIO_GANTT:
            case ViewType.PROJECT_GANTT:
                return ViewType.PROJECT_GANTT

            case ViewType.PORTFOLIO_CALENDAR:
            case ViewType.PROJECT_CALENDAR:
                return ViewType.PROJECT_CALENDAR

            case ViewType.PORTFOLIO_WORKLOAD:
            case ViewType.PROJECT_WORKLOAD:
                return ViewType.PROJECT_WORKLOAD

            case ViewType.PROJECT_TABLE_MAP   :
                return ViewType.PROJECT_TABLE_MAP

            case ViewType.PORTFOLIO_SUMMARY:
            case ViewType.PROJECT_SUMMARY:
                return ViewType.PORTFOLIO_SUMMARY
        }

        console.error("How come #3938272", viewType)

        // @ts-ignore
        return viewType
    }*/
}
